import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { LoadingSpinner } from '../LoadingSpinner';
import { isDemoMode, simulateApiDelay, logDemoAction } from '../../config/demo';
import { 
  Play, 
  Pause, 
  SkipForward, 
  SkipBack, 
  RotateCcw,
  Timer,
  Target,
  CheckCircle,
  Circle,
  Volume2,
  VolumeX,
  Share2,
  Bookmark,
  AlertTriangle,
  Info,
  Dumbbell,
  Clock,
  Zap,
  ChevronLeft,
  ChevronRight,
  ExternalLink,
  PlayCircle,
  PauseCircle,
  Eye,
  EyeOff,

  User,
  Heart,
  Bone,
  RefreshCw,
  Settings,
  Plus,
  Minus,
  Edit3,
  ArrowUpDown
} from 'lucide-react';
import WorkoutTimerModal from './WorkoutTimerModal';
import WorkoutTimerCard from './WorkoutTimerCard';

interface Exercise {
  id: string;
  name: string;
  description: string;
  category: string;
  equipment: string;
  difficulty: string;
  muscleGroups: string[];
  instructions: string[];
  cues: string[];
  safetyNotes: string[];
  videoUrl?: string;
  videoSource?: 'youtube' | 'vimeo' | 'instagram' | 'tiktok' | 'custom';
  videoId?: string;
  videoChannel?: string;
  videoChannelUrl?: string;
  videoAttribution?: string;
  videoDuration?: number;
  videoThumbnail?: string;
}

interface WorkoutSet {
  id: string;
  reps: number | null;
  weight: number | null;
  rpe: number | null; // Rate of Perceived Exertion (1-10)
  restTime: number; // in seconds
  completed: boolean;
  notes?: string;
}

interface WorkoutExercise {
  id: string;
  exercise: Exercise;
  sets: WorkoutSet[];
  targetSets: number;
  targetReps: number;
  targetWeight?: number;
  restBetweenSets: number; // in seconds
  completed: boolean;
  notes?: string;
}

interface Workout {
  id: string;
  name: string;
  description: string;
  exercises: WorkoutExercise[];
  estimatedDuration: number; // in minutes
  difficulty: string;
  createdAt: Date;
}

const WorkoutPlayerPanel: React.FC = () => {
  const [workout, setWorkout] = useState<Workout | null>(null);
  const [currentExerciseIndex, setCurrentExerciseIndex] = useState(0);
  const [currentSetIndex, setCurrentSetIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [showVideo, setShowVideo] = useState(true);
  const [showUnclippedItems, setShowUnclippedItems] = useState(true);
  const [timer, setTimer] = useState(0);
  const [restTimer, setRestTimer] = useState(0);
  const [isResting, setIsResting] = useState(false);
  const [workoutStartTime, setWorkoutStartTime] = useState<Date | null>(null);
  const [workoutEndTime, setWorkoutEndTime] = useState<Date | null>(null);
  const [completedExercises, setCompletedExercises] = useState<Set<string>>(new Set());
  const [completedSets, setCompletedSets] = useState<Set<string>>(new Set());

  const [showTimerModal, setShowTimerModal] = useState(false);

  const [showSwapModal, setShowSwapModal] = useState(false);
  const [showModifyModal, setShowModifyModal] = useState(false);
  const [availableExercises, setAvailableExercises] = useState<Exercise[]>([]);

  useEffect(() => {
    const loadWorkout = async () => {
      try {
        await simulateApiDelay();
        
        if (isDemoMode()) {
          logDemoAction('Workout loaded', {
            component: 'WorkoutPlayerPanel',
            workoutId: 'demo-workout-456',
            timestamp: new Date().toISOString()
          });
        }
        
        loadSampleWorkout();
      } catch (error) {
        console.error('Error loading workout:', error);
      }
    };

    loadWorkout();
  }, []);

  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (isPlaying && !isResting) {
      interval = setInterval(() => {
        setTimer(prev => prev + 1);
      }, 1000);
    } else if (isResting && restTimer > 0) {
      interval = setInterval(() => {
        setRestTimer(prev => {
          if (prev <= 1) {
            setIsResting(false);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [isPlaying, isResting, restTimer]);

  const loadSampleWorkout = () => {
    const sampleWorkout: Workout = {
      id: 'workout_1',
      name: 'Upper Body Strength',
      description: 'A comprehensive upper body workout focusing on chest, shoulders, and arms.',
      estimatedDuration: 45,
      difficulty: 'intermediate',
      createdAt: new Date(),
      exercises: [
        {
          id: 'ex_1',
          exercise: {
            id: 'ex_1',
            name: 'Push-up',
            description: 'A classic bodyweight exercise that targets the chest, shoulders, and triceps.',
            category: 'strength',
            equipment: 'bodyweight',
            difficulty: 'beginner',
            muscleGroups: ['chest', 'shoulders', 'triceps'],
            instructions: [
              'Start in a plank position with hands slightly wider than shoulders',
              'Lower your body until chest nearly touches the floor',
              'Push back up to starting position',
              'Keep core tight throughout the movement'
            ],
            cues: [
              'Keep body in straight line',
              'Engage core',
              'Full range of motion'
            ],
            safetyNotes: [
              'Keep wrists aligned under shoulders',
              'Stop if you feel wrist pain'
            ],
            videoUrl: 'https://www.youtube.com/watch?v=4uQtPyWkXcc',
            videoSource: 'youtube',
            videoId: '4uQtPyWkXcc',
            videoChannel: 'Athlean-X',
            videoChannelUrl: 'https://www.youtube.com/@athleanx',
            videoAttribution: 'Video provided by Athlean-X — used for educational purposes only',
            videoDuration: 180,
            videoThumbnail: 'https://img.youtube.com/vi/4uQtPyWkXcc/maxresdefault.jpg'
          },
          sets: [
            { id: 'set_1', reps: null, weight: null, rpe: null, restTime: 60, completed: false },
            { id: 'set_2', reps: null, weight: null, rpe: null, restTime: 60, completed: false },
            { id: 'set_3', reps: null, weight: null, rpe: null, restTime: 60, completed: false }
          ],
          targetSets: 3,
          targetReps: 12,
          restBetweenSets: 60,
          completed: false
        },
        {
          id: 'ex_2',
          exercise: {
            id: 'ex_2',
            name: 'Goblet Squat',
            description: 'A beginner-friendly squat variation using a dumbbell or kettlebell.',
            category: 'strength',
            equipment: 'dumbbells',
            difficulty: 'beginner',
            muscleGroups: ['glutes', 'quads', 'hamstrings'],
            instructions: [
              'Hold a dumbbell or kettlebell at chest level',
              'Stand with feet shoulder-width apart',
              'Lower down into a squat position',
              'Keep chest up and core engaged',
              'Return to standing position'
            ],
            cues: [
              'Chest up',
              'Knees track over toes',
              'Full depth',
              'Drive through heels'
            ],
            safetyNotes: [
              'Keep weight close to body',
              'Maintain neutral spine',
              'Stop if you feel knee pain'
            ],
            videoUrl: 'https://www.youtube.com/watch?v=4uQtPyWkXcc',
            videoSource: 'youtube',
            videoId: '4uQtPyWkXcc',
            videoChannel: 'Fitness Blender',
            videoChannelUrl: 'https://www.youtube.com/@fitnessblender',
            videoAttribution: 'Video provided by Fitness Blender — used for educational purposes only',
            videoDuration: 120,
            videoThumbnail: 'https://img.youtube.com/vi/4uQtPyWkXcc/maxresdefault.jpg'
          },
          sets: [
            { id: 'set_4', reps: null, weight: null, rpe: null, restTime: 90, completed: false },
            { id: 'set_5', reps: null, weight: null, rpe: null, restTime: 90, completed: false },
            { id: 'set_6', reps: null, weight: null, rpe: null, restTime: 90, completed: false }
          ],
          targetSets: 3,
          targetReps: 15,
          targetWeight: 20,
          restBetweenSets: 90,
          completed: false
        }
      ]
    };

    setWorkout(sampleWorkout);
  };

  const startWorkout = () => {
    setIsPlaying(true);
    setWorkoutStartTime(new Date());
  };

  const pauseWorkout = () => {
    setIsPlaying(false);
  };

  const completeSet = (exerciseId: string, setId: string, reps: number, weight?: number, rpe?: number) => {
    if (!workout) return;

    const updatedWorkout = { ...workout };
    const exercise = updatedWorkout.exercises.find(ex => ex.id === exerciseId);
    if (!exercise) return;

    const set = exercise.sets.find(s => s.id === setId);
    if (!set) return;

    set.reps = reps;
    set.weight = weight || null;
    set.rpe = rpe || null;
    set.completed = true;

    setCompletedSets(prev => new Set([...prev, setId]));

    // Check if all sets are completed
    const allSetsCompleted = exercise.sets.every(s => s.completed);
    if (allSetsCompleted) {
      exercise.completed = true;
      setCompletedExercises(prev => new Set([...prev, exerciseId]));
    }

    setWorkout(updatedWorkout);

    // Start rest timer
    setIsResting(true);
    setRestTimer(set.restTime);
  };

  const nextExercise = () => {
    if (!workout) return;
    if (currentExerciseIndex < workout.exercises.length - 1) {
      setCurrentExerciseIndex(prev => prev + 1);
      setCurrentSetIndex(0);
    }
  };

  const previousExercise = () => {
    if (currentExerciseIndex > 0) {
      setCurrentExerciseIndex(prev => prev - 1);
      setCurrentSetIndex(0);
    }
  };

  const finishWorkout = () => {
    setIsPlaying(false);
    setWorkoutEndTime(new Date());
    
    if (isDemoMode()) {
      logDemoAction('Workout completed', {
        workoutId: workout?.id,
        startTime: workoutStartTime,
        endTime: new Date(),
        completedSets: Array.from(completedSets),
        completedExercises: Array.from(completedExercises),
        timestamp: new Date().toISOString()
      });
      alert('Workout completed! Great job! (Demo Mode)');
    } else {
      // Save workout results to API
      console.log('Workout completed!', {
        startTime: workoutStartTime,
        endTime: new Date(),
        exercises: workout?.exercises,
        completedSets: Array.from(completedSets),
        completedExercises: Array.from(completedExercises)
      });
    }
  };

  const formatTime = (seconds: number): string => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  const generateEmbedUrl = (videoId: string, source: string): string => {
    switch (source) {
      case 'youtube':
        return `https://www.youtube.com/embed/${videoId}`;
      case 'vimeo':
        return `https://player.vimeo.com/video/${videoId}`;
      case 'instagram':
        return `https://www.instagram.com/p/${videoId}/embed/`;
      case 'tiktok':
        return `https://www.tiktok.com/embed/v2/${videoId}`;
      default:
        return '';
    }
  };

  const getProgressPercentage = (): number => {
    if (!workout) return 0;
    const totalSets = workout.exercises.reduce((sum, ex) => sum + ex.sets.length, 0);
    return (completedSets.size / totalSets) * 100;
  };

  const loadAvailableExercises = async () => {
    // Mock available exercises for swapping
    const exercises: Exercise[] = [
      {
        id: 'ex_3',
        name: 'Incline Push-up',
        description: 'A modified push-up with hands elevated for easier execution.',
        category: 'strength',
        equipment: 'bodyweight',
        difficulty: 'beginner',
        muscleGroups: ['chest', 'shoulders', 'triceps'],
        instructions: ['Place hands on elevated surface', 'Perform push-up with proper form'],
        cues: ['Keep core tight', 'Full range of motion'],
        safetyNotes: ['Maintain straight line', 'Control the movement'],
        videoUrl: 'https://www.youtube.com/watch?v=4uQtPyWkXcc',
        videoSource: 'youtube',
        videoId: '4uQtPyWkXcc',
        videoChannel: 'Fitness Blender',
        videoChannelUrl: 'https://www.youtube.com/@fitnessblender',
        videoAttribution: 'Video provided by Fitness Blender',
        videoDuration: 90,
        videoThumbnail: 'https://img.youtube.com/vi/4uQtPyWkXcc/maxresdefault.jpg'
      },
      {
        id: 'ex_4',
        name: 'Dumbbell Press',
        description: 'A chest exercise using dumbbells for strength building.',
        category: 'strength',
        equipment: 'dumbbells',
        difficulty: 'intermediate',
        muscleGroups: ['chest', 'shoulders', 'triceps'],
        instructions: ['Lie on bench with dumbbells', 'Press up and down with control'],
        cues: ['Keep core engaged', 'Full range of motion'],
        safetyNotes: ['Use proper form', 'Start with lighter weight'],
        videoUrl: 'https://www.youtube.com/watch?v=4uQtPyWkXcc',
        videoSource: 'youtube',
        videoId: '4uQtPyWkXcc',
        videoChannel: 'Athlean-X',
        videoChannelUrl: 'https://www.youtube.com/@athleanx',
        videoAttribution: 'Video provided by Athlean-X',
        videoDuration: 120,
        videoThumbnail: 'https://img.youtube.com/vi/4uQtPyWkXcc/maxresdefault.jpg'
      }
    ];
    setAvailableExercises(exercises);
  };

  const swapExercise = (newExercise: Exercise) => {
    if (!workout) return;
    
    const updatedWorkout = { ...workout };
    const currentExercise = updatedWorkout.exercises[currentExerciseIndex];
    
    // Replace the exercise while maintaining sets structure
    currentExercise.exercise = newExercise;
    
    setWorkout(updatedWorkout);
    setShowSwapModal(false);
    
    if (isDemoMode()) {
      logDemoAction('Exercise swapped', {
        workoutId: workout.id,
        oldExercise: currentExercise.exercise.name,
        newExercise: newExercise.name,
        timestamp: new Date().toISOString()
      });
    }
  };

  const modifyWorkout = (modifications: { sets?: number; reps?: number; weight?: number; rest?: number }) => {
    if (!workout) return;
    
    const updatedWorkout = { ...workout };
    const currentExercise = updatedWorkout.exercises[currentExerciseIndex];
    
    if (modifications.sets !== undefined) {
      currentExercise.targetSets = modifications.sets;
      // Adjust sets array
      const newSets = Array.from({ length: modifications.sets }, (_, i) => ({
        id: `set_${i + 1}`,
        reps: null,
        weight: null,
        rpe: null,
        restTime: currentExercise.restBetweenSets,
        completed: false
      }));
      currentExercise.sets = newSets;
    }
    
    if (modifications.reps !== undefined) {
      currentExercise.targetReps = modifications.reps;
    }
    
    if (modifications.weight !== undefined) {
      currentExercise.targetWeight = modifications.weight;
    }
    
    if (modifications.rest !== undefined) {
      currentExercise.restBetweenSets = modifications.rest;
      currentExercise.sets.forEach(set => {
        set.restTime = modifications.rest!;
      });
    }
    
    setWorkout(updatedWorkout);
    setShowModifyModal(false);
    
    if (isDemoMode()) {
      logDemoAction('Workout modified', {
        workoutId: workout.id,
        exerciseId: currentExercise.id,
        modifications,
        timestamp: new Date().toISOString()
      });
    }
  };

  if (!workout) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" variant="fitness" text="Loading workout..." />
      </div>
    );
  }

  const currentExercise = workout.exercises[currentExerciseIndex];
  const currentSet = currentExercise.sets[currentSetIndex];

  return (
    <div className="space-y-6">
      {/* Independent Timer Card */}
      <WorkoutTimerCard 
        workoutName={workout.name}
        isWorkoutActive={isPlaying}
        onTimerStart={() => {
          if (!isPlaying) {
            startWorkout();
          }
        }}
        onTimerStop={() => {
          if (isPlaying) {
            pauseWorkout();
          }
        }}
      />

      {/* Workout Header */}
      <div className="sculptd-card-elevated sculptd-sheen sculptd-container-padding" style={{
        boxShadow: 'var(--shadow-card-separation)',
        padding: '16px'
      }}>
        <div className="mb-6">
          <div className="flex items-center justify-between">
            <div className="space-y-3">
              <h2 className="text-2xl font-bold" style={{ 
                color: 'var(--color-text)',
                textShadow: '0 2px 4px rgba(0,0,0,0.1)'
              }}>{workout.name}</h2>
              <p className="text-base" style={{ 
                color: 'var(--color-text-muted)',
                lineHeight: '1.6'
              }}>{workout.description}</p>
            </div>
          </div>
        </div>
        
        <div className="space-y-6">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
            <div className="flex flex-col sm:flex-row sm:items-center space-y-4 sm:space-y-0 sm:space-x-6">
              <span className="px-3 py-1 rounded-full text-sm font-medium w-fit" style={{ 
                background: 'var(--gradient-cta-primary)', 
                color: 'var(--color-white)',
                boxShadow: 'var(--shadow-lg)'
              }}>
                {workout.difficulty}
              </span>
              <div className="grid grid-cols-2 gap-4 sm:flex sm:items-center sm:space-x-6">
                <div className="flex flex-col items-center sm:items-start space-y-1">
                  <div className="flex items-center space-x-2">
                    <Clock className="w-5 h-5" style={{ color: 'var(--color-primary)' }} />
                    <span className="text-xl md:text-2xl font-bold" style={{ 
                      color: 'var(--color-text)',
                      textShadow: '0 1px 2px rgba(0,0,0,0.05)'
                    }}>{workout.estimatedDuration}</span>
                  </div>
                  <span className="text-sm" style={{ 
                    color: 'var(--color-text-muted)',
                    textShadow: '0 1px 2px rgba(0,0,0,0.05)'
                  }}>min</span>
                </div>
                <div className="flex flex-col items-center sm:items-start space-y-1">
                  <div className="flex items-center space-x-2">
                    <Target className="w-5 h-5" style={{ color: 'var(--color-primary)' }} />
                    <span className="text-xl md:text-2xl font-bold" style={{ 
                      color: 'var(--color-text)',
                      textShadow: '0 1px 2px rgba(0,0,0,0.05)'
                    }}>{workout.exercises.length}</span>
                  </div>
                  <span className="text-sm" style={{ 
                    color: 'var(--color-text-muted)',
                    textShadow: '0 1px 2px rgba(0,0,0,0.05)'
                  }}>exercises</span>
                </div>
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-2 md:gap-3">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowVideo(!showVideo)}
                style={{
                  minHeight: '44px',
                  padding: '8px 16px'
                }}
              >
                {showVideo ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                <span className="ml-2">{showVideo ? 'Hide' : 'Show'}</span>
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowUnclippedItems(!showUnclippedItems)}
                style={{
                  minHeight: '44px',
                  padding: '8px 16px'
                }}
              >
                {showUnclippedItems ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                <span className="ml-2 hidden sm:inline">{showUnclippedItems ? 'Hide' : 'Show'}</span>
                <span className="ml-2 sm:hidden">Hide</span>
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {/* Share workout */}}
                style={{
                  minHeight: '44px',
                  padding: '8px 16px'
                }}
              >
                <Share2 className="w-4 h-4" />
                <span className="ml-2 hidden sm:inline">Share</span>
              </Button>
            </div>
          </div>

          {/* Progress Bar */}
          <div>
            <div className="flex items-center justify-between text-sm mb-2" style={{ color: 'var(--color-text-muted)', textShadow: '0 1px 2px rgba(0,0,0,0.05)' }}>
              <span>Progress</span>
              <span>{Math.round(getProgressPercentage())}%</span>
            </div>
            <div className="w-full rounded-full h-3" style={{ background: 'var(--color-background-secondary)' }}>
              <div 
                className="h-3 rounded-full transition-all duration-300"
                style={{ 
                  width: `${getProgressPercentage()}%`,
                  background: 'var(--gradient-cta-primary)'
                }}
              />
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Exercise Details */}
        <div className="sculptd-card-elevated sculptd-sheen sculptd-container-padding" style={{
          boxShadow: 'var(--shadow-card-separation)',
          padding: '16px'
        }}>
          <div className="mb-6">
            <div className="flex items-center justify-between">
              <div className="space-y-3">
                <h3 className="text-xl font-semibold" style={{ 
                  color: 'var(--color-text)',
                  textShadow: '0 1px 2px rgba(0,0,0,0.05)'
                }}>{currentExercise.exercise.name}</h3>
                <p className="text-base" style={{ 
                  color: 'var(--color-text-muted)',
                  lineHeight: '1.6'
                }}>{currentExercise.exercise.description}</p>
              </div>
              <div className="text-right ml-8">
                <div className="text-2xl font-bold" style={{ 
                  color: 'var(--color-success)',
                  textShadow: '0 1px 2px rgba(0,0,0,0.05)'
                }}>
                  {currentExerciseIndex + 1}/{workout.exercises.length}
                </div>
                <div className="text-sm" style={{ color: 'var(--color-text-muted)', textShadow: '0 1px 2px rgba(0,0,0,0.05)' }}>Exercise</div>
              </div>
            </div>
          </div>
          <div className="space-y-6">
            {/* Exercise Info with Friendly Options */}
            {showUnclippedItems && (
              <div className="space-y-4">
                <div className="flex items-center space-x-4">
                  <span className="px-4 py-2 rounded-full text-sm font-medium" style={{ 
                    background: 'var(--color-teal)', 
                    color: 'var(--color-white)',
                    minHeight: '32px',
                    display: 'flex',
                    alignItems: 'center',
                    textTransform: 'capitalize'
                  }}>
                    {currentExercise.exercise.difficulty}
                  </span>
                  
                  <span className="px-4 py-2 rounded-full text-sm font-medium flex items-center space-x-2" style={{ 
                    background: 'var(--color-lavender)', 
                    color: 'var(--color-white)',
                    minHeight: '32px'
                  }}>
                    {currentExercise.exercise.equipment === 'bodyweight' ? (
                      <User className="w-4 h-4" />
                    ) : (
                      <Dumbbell className="w-4 h-4" />
                    )}
                    <span className="capitalize">{currentExercise.exercise.equipment.replace('_', ' ')}</span>
                  </span>
                  
                  <span className="px-4 py-2 rounded-full text-sm font-medium flex items-center space-x-2" style={{ 
                    background: 'var(--color-blush-pink)', 
                    color: 'var(--color-white)',
                    minHeight: '32px'
                  }}>
                    <Bone className="w-4 h-4" />
                    <span>Friendly</span>
                  </span>
                </div>
                
                {/* Swap/Modify Options */}
                <div className="flex flex-wrap gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      loadAvailableExercises();
                      setShowSwapModal(true);
                    }}
                    style={{
                      minHeight: '36px',
                      padding: '6px 12px',
                      background: 'var(--gradient-spa)',
                      color: 'var(--color-white)',
                      border: 'none'
                    }}
                  >
                    <RotateCcw className="w-4 h-4 mr-1" />
                    Swap Exercise
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setShowModifyModal(true)}
                    style={{
                      minHeight: '36px',
                      padding: '6px 12px',
                      background: 'var(--gradient-accent-button)',
                      color: 'var(--color-white)',
                      border: 'none'
                    }}
                  >
                    <Target className="w-4 h-4 mr-1" />
                    Modify
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setShowTimerModal(true)}
                    style={{
                      minHeight: '36px',
                      padding: '6px 12px',
                      background: 'var(--gradient-primary)',
                      color: 'var(--color-white)',
                      border: 'none'
                    }}
                  >
                    <Timer className="w-4 h-4 mr-1" />
                    Timer
                  </Button>
                </div>
              </div>
            )}

            {/* Target Sets/Reps */}
            <div className="p-6 rounded-lg" style={{ background: 'var(--color-background)' }}>
              <h4 className="font-medium mb-4 text-lg" style={{ color: 'var(--color-text)' }}>Target</h4>
              <div className="grid grid-cols-3 gap-6 text-center">
                <div>
                  <div className="text-2xl font-bold" style={{ color: 'var(--color-primary)' }}>{currentExercise.targetSets}</div>
                  <div className="text-sm mt-1" style={{ color: 'var(--color-text-muted)', textShadow: '0 1px 2px rgba(0,0,0,0.05)' }}>Sets</div>
                </div>
                <div>
                  <div className="text-2xl font-bold" style={{ color: 'var(--color-primary)' }}>{currentExercise.targetReps}</div>
                  <div className="text-sm mt-1" style={{ color: 'var(--color-text-muted)', textShadow: '0 1px 2px rgba(0,0,0,0.05)' }}>Reps</div>
                </div>
                {currentExercise.targetWeight && (
                  <div>
                    <div className="text-2xl font-bold" style={{ color: 'var(--color-primary)' }}>{currentExercise.targetWeight}</div>
                    <div className="text-sm mt-1" style={{ color: 'var(--color-text-muted)', textShadow: '0 1px 2px rgba(0,0,0,0.05)' }}>Weight (lbs)</div>
                  </div>
                )}
              </div>
            </div>

            {/* Current Set */}
            <div className="p-6 rounded-lg" style={{ background: 'var(--color-primary-light)' }}>
              <h4 className="font-medium mb-4 text-lg" style={{ color: 'var(--color-text)' }}>Set {currentSetIndex + 1}</h4>
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: 'var(--color-text)' }}>Reps</label>
                  <Input
                    type="number"
                    placeholder="0"
                    value={currentSet.reps || ''}
                    onChange={(e) => {
                      const updatedWorkout = { ...workout };
                      const set = updatedWorkout.exercises[currentExerciseIndex].sets[currentSetIndex];
                      set.reps = parseInt(e.target.value) || null;
                      setWorkout(updatedWorkout);
                    }}
                  />
                </div>
                {currentExercise.targetWeight && (
                  <div>
                    <label className="block text-sm font-medium mb-2" style={{ color: 'var(--color-text)' }}>Weight (lbs)</label>
                    <Input
                      type="number"
                      placeholder="0"
                      value={currentSet.weight || ''}
                      onChange={(e) => {
                        const updatedWorkout = { ...workout };
                        const set = updatedWorkout.exercises[currentExerciseIndex].sets[currentSetIndex];
                        set.weight = parseFloat(e.target.value) || null;
                        setWorkout(updatedWorkout);
                      }}
                    />
                  </div>
                )}
              </div>
              <div className="mt-4">
                <label className="block text-sm font-medium mb-2" style={{ color: 'var(--color-text)' }}>RPE (1-10)</label>
                <Input
                  type="number"
                  min="1"
                  max="10"
                  placeholder="0"
                  value={currentSet.rpe || ''}
                  onChange={(e) => {
                    const updatedWorkout = { ...workout };
                    const set = updatedWorkout.exercises[currentExerciseIndex].sets[currentSetIndex];
                    set.rpe = parseInt(e.target.value) || null;
                    setWorkout(updatedWorkout);
                  }}
                />
              </div>
              <Button
                className="w-full mt-6"
                onClick={() => {
                  if (currentSet.reps) {
                    completeSet(
                      currentExercise.id,
                      currentSet.id,
                      currentSet.reps,
                      currentSet.weight || undefined,
                      currentSet.rpe || undefined
                    );
                    if (currentSetIndex < currentExercise.sets.length - 1) {
                      setCurrentSetIndex(prev => prev + 1);
                    }
                  }
                }}
                disabled={!currentSet.reps}
                style={{ minHeight: '44px' }}
              >
                <CheckCircle className="w-4 h-4 mr-2" />
                Complete Set
              </Button>
            </div>

            {/* Rest Timer */}
            {isResting && (
              <div className="p-6 rounded-lg text-center" style={{ background: 'var(--color-warning-light)' }}>
                <div className="text-3xl font-bold mb-3" style={{ color: 'var(--color-warning)' }}>
                  {formatTime(restTimer)}
                </div>
                <div className="text-sm mb-4" style={{ color: 'var(--color-text-muted)' }}>Rest Time</div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setIsResting(false);
                    setRestTimer(0);
                  }}
                  style={{ minHeight: '36px' }}
                >
                  Skip Rest
                </Button>
              </div>
            )}

            {/* Exercise Actions */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    loadAvailableExercises();
                    setShowSwapModal(true);
                  }}
                  className="flex items-center space-x-2"
                >
                  <ArrowUpDown className="w-4 h-4" />
                  <span>Swap Exercise</span>
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowModifyModal(true)}
                  className="flex items-center space-x-2"
                >
                  <Edit3 className="w-4 h-4" />
                  <span>Modify</span>
                </Button>
              </div>
            </div>

            {/* Navigation */}
            {showUnclippedItems && (
              <div className="flex items-center justify-between gap-4">
                <Button
                  variant="outline"
                  onClick={previousExercise}
                  disabled={currentExerciseIndex === 0}
                  className="flex-1"
                  style={{ minHeight: '44px' }}
                >
                  <ChevronLeft className="w-4 h-4 mr-2" />
                  Previous
                </Button>
                <Button
                  variant="outline"
                  onClick={nextExercise}
                  disabled={currentExerciseIndex === workout.exercises.length - 1}
                  className="flex-1"
                  style={{ minHeight: '44px' }}
                >
                  Next
                  <ChevronRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            )}
          </div>
        </div>

        {/* Video Section */}
        {showVideo && currentExercise.exercise.videoUrl && (
          <div className="sculptd-card-elevated sculptd-sheen sculptd-container-padding" style={{
            boxShadow: 'var(--shadow-card-separation)',
            padding: '16px'
          }}>
            <div className="mb-6">
              <h3 className="flex items-center space-x-3 text-xl font-semibold" style={{ 
                color: 'var(--color-text)',
                textShadow: '0 2px 4px rgba(0,0,0,0.1)'
              }}>
                <PlayCircle className="w-6 h-6" style={{ color: 'var(--color-primary)' }} />
                <span>Exercise Demonstration</span>
              </h3>
            </div>
            <div className="space-y-6">
              {/* Video Player */}
              <div className="relative w-full h-64 bg-black rounded-lg overflow-hidden">
                <iframe
                  src={generateEmbedUrl(currentExercise.exercise.videoId!, currentExercise.exercise.videoSource!)}
                  className="w-full h-full"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  title={currentExercise.exercise.name}
                />
              </div>

              {/* Video Attribution */}
              <div className="text-xs rounded-lg sculptd-container-padding" style={{ 
                color: 'var(--color-text-muted)',
                background: 'var(--color-background)',
                padding: '16px'
              }}>
                <div dangerouslySetInnerHTML={{ __html: currentExercise.exercise.videoAttribution || '' }} />
                <div className="mt-2 flex items-center space-x-2">
                  <AlertTriangle className="w-4 h-4" />
                  <span>Always consult your physician before beginning any exercise program. Technique videos are for reference only.</span>
                </div>
              </div>

              {/* Exercise Instructions */}
              <div>
                <h4 className="font-medium mb-4 text-lg" style={{ color: 'var(--color-text)' }}>Instructions</h4>
                <ol className="text-sm space-y-2" style={{ color: 'var(--color-text-muted)' }}>
                  {currentExercise.exercise.instructions.map((instruction, index) => (
                    <li key={index} className="flex items-start space-x-3">
                      <span className="font-medium text-base" style={{ color: 'var(--color-primary)' }}>{index + 1}.</span>
                      <span className="leading-relaxed">{instruction}</span>
                    </li>
                  ))}
                </ol>
              </div>

              {/* Key Cues */}
              <div>
                <h4 className="font-medium mb-4 text-lg" style={{ color: 'var(--color-text)' }}>Key Cues</h4>
                <ul className="text-sm space-y-2" style={{ color: 'var(--color-text-muted)' }}>
                  {currentExercise.exercise.cues.map((cue, index) => (
                    <li key={index} className="flex items-start space-x-3">
                      <span className="text-base" style={{ color: 'var(--color-success)' }}>•</span>
                      <span className="leading-relaxed">{cue}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Safety Notes */}
              <div>
                <h4 className="font-medium mb-4 text-lg flex items-center space-x-2">
                  <AlertTriangle className="w-5 h-5 text-red-500" />
                  <span style={{ color: 'var(--color-text)' }}>Safety Notes</span>
                </h4>
                <ul className="text-sm space-y-2" style={{ color: 'var(--color-text-muted)' }}>
                  {currentExercise.exercise.safetyNotes.map((note, index) => (
                    <li key={index} className="flex items-start space-x-3">
                      <span className="text-base" style={{ color: 'var(--color-error)' }}>⚠</span>
                      <span className="leading-relaxed">{note}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Workout Controls */}
      <div className="sculptd-card-elevated sculptd-sheen sculptd-container-padding" style={{
        boxShadow: 'var(--shadow-card-separation)',
        padding: '16px'
      }}>
        <div className="flex items-center justify-center space-x-6">
          {!isPlaying ? (
            <Button
              size="lg"
              onClick={startWorkout}
              className="sculptd-cta-button sculptd-cta-button-mobile"
              style={{ minHeight: '56px' }}
            >
              <Play className="w-6 h-6 mr-3" />
              Start Workout
            </Button>
          ) : (
            <>
              <Button
                variant="outline"
                size="lg"
                onClick={pauseWorkout}
                className="px-6 py-4 text-lg font-semibold"
                style={{ minHeight: '56px' }}
              >
                <Pause className="w-6 h-6 mr-3" />
                Pause
              </Button>

              <Button
                size="lg"
                onClick={finishWorkout}
                className="bg-red-600 hover:bg-red-700 px-6 py-4 text-lg font-semibold"
                style={{ minHeight: '56px' }}
              >
                <CheckCircle className="w-6 h-6 mr-3" />
                Finish Workout
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Workout Timer Modal */}
      <WorkoutTimerModal
        isOpen={showTimerModal}
        onClose={() => setShowTimerModal(false)}
        workoutName={workout.name}
        totalDuration={workout.estimatedDuration * 60}
      />

      {/* Swap Exercise Modal */}
      {showSwapModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4 max-h-[80vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">Swap Exercise</h3>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowSwapModal(false)}
              >
                ×
              </Button>
            </div>
            <p className="text-sm text-gray-600 mb-4">
              Choose a replacement exercise for "{currentExercise.exercise.name}"
            </p>
            <div className="space-y-3">
              {availableExercises.map((exercise) => (
                <div
                  key={exercise.id}
                  className="border border-gray-200 rounded-lg p-3 hover:border-blue-300 cursor-pointer transition-colors"
                  onClick={() => swapExercise(exercise)}
                >
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                      <Dumbbell className="w-5 h-5 text-blue-600" />
                    </div>
                    <div className="flex-1">
                      <h4 className="font-medium text-gray-900">{exercise.name}</h4>
                      <p className="text-sm text-gray-600">{exercise.description}</p>
                      <div className="flex items-center space-x-2 mt-1">
                        <span className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded">
                          {exercise.difficulty}
                        </span>
                        <span className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded">
                          {exercise.equipment}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <div className="flex justify-end space-x-2 mt-4">
              <Button
                variant="outline"
                onClick={() => setShowSwapModal(false)}
              >
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Modify Workout Modal */}
      {showModifyModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">Modify Exercise</h3>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowModifyModal(false)}
              >
                ×
              </Button>
            </div>
            <p className="text-sm text-gray-600 mb-4">
              Adjust the parameters for "{currentExercise.exercise.name}"
            </p>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Sets</label>
                  <Input
                    type="number"
                    defaultValue={currentExercise.targetSets}
                    onChange={(e) => {
                      const value = parseInt(e.target.value);
                      if (value > 0) {
                        modifyWorkout({ sets: value });
                      }
                    }}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Reps</label>
                  <Input
                    type="number"
                    defaultValue={currentExercise.targetReps}
                    onChange={(e) => {
                      const value = parseInt(e.target.value);
                      if (value > 0) {
                        modifyWorkout({ reps: value });
                      }
                    }}
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Weight (lbs)</label>
                  <Input
                    type="number"
                    defaultValue={currentExercise.targetWeight || 0}
                    onChange={(e) => {
                      const value = parseFloat(e.target.value);
                      if (value >= 0) {
                        modifyWorkout({ weight: value });
                      }
                    }}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Rest (sec)</label>
                  <Input
                    type="number"
                    defaultValue={currentExercise.restBetweenSets}
                    onChange={(e) => {
                      const value = parseInt(e.target.value);
                      if (value > 0) {
                        modifyWorkout({ rest: value });
                      }
                    }}
                  />
                </div>
              </div>
            </div>
            <div className="flex justify-end space-x-2 mt-4">
              <Button
                variant="outline"
                onClick={() => setShowModifyModal(false)}
              >
                Cancel
              </Button>
              <Button
                onClick={() => setShowModifyModal(false)}
              >
                Apply Changes
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WorkoutPlayerPanel;
